<template>
  <b-modal
    v-model="showModal"
    title="Period"
    centered
    hide-footer
    size="lg"
    modal-class="date-picker-modal"
    @hide="closeModal"
  >
    <div class="date-picker-content">
      <!-- Period Buttons -->
      <div class="period-buttons mb-4">
        <b-button
          v-for="period in periodOptions"
          :key="period"
          :variant="selectedPeriod === period ? 'primary' : 'outline-primary'"
          class="period-btn me-2 mb-2"
          @click="selectPeriod(period)"
        >
          {{ period }}
        </b-button>
      </div>

      <!-- Date Range Inputs -->
      <div class="row mb-4">
        <div class="col-6">
          <label class="form-label">Start Date</label>
          <b-form-input
            type="text"
            v-model="formattedStartDate"
            readonly
            class="date-input"
          ></b-form-input>
        </div>
        <div class="col-6">
          <label class="form-label">End Date</label>
          <b-form-input
            type="text"
            v-model="formattedEndDate"
            readonly
            class="date-input"
          ></b-form-input>
        </div>
      </div>

      <!-- Calendar -->
      <div class="row">
        <div class="col-6">
          <v-date-picker
            v-model="dateRange.start"
            :max-date="dateRange.end"
            is-inline
            class="custom-calendar"
          />
        </div>
        <div class="col-6">
          <v-date-picker
            v-model="dateRange.end"
            :min-date="dateRange.start"
            is-inline
            class="custom-calendar"
          />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex mt-4">
        <b-button block variant="outline-primary" class="me-2" @click="closeModal">
          Cancel
        </b-button>
        <b-button block variant="primary" class="mt-0" @click="applyDateRange">
          Apply
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DatePickerModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialPeriod: {
      type: String,
      default: 'This Year'
    },
    initialStartDate: {
      type: Date,
      default: null
    },
    initialEndDate: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      selectedPeriod: this.initialPeriod,
      dateRange: {
        start: this.initialStartDate || new Date(),
        end: this.initialEndDate || new Date()
      },
      periodOptions: [
        'Today',
        'Yesterday', 
        'This Week',
        'Last Week',
        'This Month',
        'Last Month',
        'Q1',
        'Q2',
        'Q3',
        'Q4',
        'Semester 1',
        'Semester 2',
        'This Year',
        'Last Year'
      ]
    };
  },
  computed: {
    showModal: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    formattedStartDate() {
      return moment(this.dateRange.start).format('DD MMMM YYYY');
    },
    formattedEndDate() {
      return moment(this.dateRange.end).format('DD MMMM YYYY');
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset to initial values when modal opens
        this.selectedPeriod = this.initialPeriod;
        this.dateRange.start = this.initialStartDate || new Date();
        this.dateRange.end = this.initialEndDate || new Date();
      }
    }
  },
  methods: {
    selectPeriod(period) {
      this.selectedPeriod = period;
      const today = new Date();
      
      switch(period) {
        case 'Today':
          this.dateRange.start = new Date(today);
          this.dateRange.end = new Date(today);
          break;
        case 'Yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          this.dateRange.start = yesterday;
          this.dateRange.end = yesterday;
          break;
        case 'This Week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          this.dateRange.start = startOfWeek;
          this.dateRange.end = new Date(today);
          break;
        case 'Last Week':
          const lastWeekStart = new Date(today);
          lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
          const lastWeekEnd = new Date(lastWeekStart);
          lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
          this.dateRange.start = lastWeekStart;
          this.dateRange.end = lastWeekEnd;
          break;
        case 'This Month':
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          this.dateRange.start = startOfMonth;
          this.dateRange.end = new Date(today);
          break;
        case 'Last Month':
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
          this.dateRange.start = lastMonthStart;
          this.dateRange.end = lastMonthEnd;
          break;
        case 'Q1':
          this.dateRange.start = new Date(today.getFullYear(), 0, 1);
          this.dateRange.end = new Date(today.getFullYear(), 2, 31);
          break;
        case 'Q2':
          this.dateRange.start = new Date(today.getFullYear(), 3, 1);
          this.dateRange.end = new Date(today.getFullYear(), 5, 30);
          break;
        case 'Q3':
          this.dateRange.start = new Date(today.getFullYear(), 6, 1);
          this.dateRange.end = new Date(today.getFullYear(), 8, 30);
          break;
        case 'Q4':
          this.dateRange.start = new Date(today.getFullYear(), 9, 1);
          this.dateRange.end = new Date(today.getFullYear(), 11, 31);
          break;
        case 'Semester 1':
          this.dateRange.start = new Date(today.getFullYear(), 0, 1);
          this.dateRange.end = new Date(today.getFullYear(), 5, 30);
          break;
        case 'Semester 2':
          this.dateRange.start = new Date(today.getFullYear(), 6, 1);
          this.dateRange.end = new Date(today.getFullYear(), 11, 31);
          break;
        case 'This Year':
          const startOfYear = new Date(today.getFullYear(), 0, 1);
          this.dateRange.start = startOfYear;
          this.dateRange.end = new Date(today);
          break;
        case 'Last Year':
          const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
          const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
          this.dateRange.start = lastYearStart;
          this.dateRange.end = lastYearEnd;
          break;
      }
    },
    applyDateRange() {
      this.$emit('apply', {
        period: this.selectedPeriod,
        startDate: this.dateRange.start,
        endDate: this.dateRange.end
      });
      this.closeModal();
    },
    closeModal() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.date-picker-content {
  padding: 1rem 0;
}

.period-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.period-btn {
  border-radius: 20px;
  padding: 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.date-input {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  font-size: 0.875rem;
}

.custom-calendar {
  width: 100%;
}

/* Override v-calendar styles if needed */
.custom-calendar >>> .vc-container {
  width: 100%;
  border: none;
}

.btn-primary {
  background-color: #B71C1C;
  border-color: #B71C1C;
}

.btn-primary:hover {
  background-color: #8B2828;
  border-color: #8B2828;
}

.btn-outline-primary {
  color: #B71C1C;
  border-color: #B71C1C;
}

.btn-outline-primary:hover {
  background-color: #B71C1C;
  border-color: #B71C1C;
  color: #fff;
}
</style>
